/**
 * ExportMenu — dropdown for outlining/exports.
 * Renders: Copy as text · Download .md · Download .txt
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, Copy, FileDown, ChevronDown, Loader2 } from "lucide-react";
import { getErrorMessage } from "../../utils/errorHandler";
import { downloadString } from "./_shared_ui";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

export default function ExportMenu({ title, sermonType, content, t = (x) => x, testId = "export-menu" }) {
  const [loading, setLoading] = useState(false);

  const fetchExport = async (format) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/pulpit/export`,
        { title, sermon_type: sermonType, content, format },
        { headers: authHeader() }
      );
      return res.data;
    } catch (err) {
      toast.error(getErrorMessage(err, "Export failed"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const copyText = async () => {
    const data = await fetchExport("text");
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const downloadFile = async (format) => {
    const data = await fetchExport(format);
    if (!data) return;
    const mime = format === "markdown" ? "text/markdown;charset=utf-8" : "text/plain;charset=utf-8";
    downloadString(data.filename, data.content, mime);
    toast.success(`Downloaded ${data.filename}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testId} disabled={loading}>
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
          {t("export") || "Export"}
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={copyText} data-testid={`${testId}-copy`}>
          <Copy className="w-3.5 h-3.5 mr-2" />
          {t("copy_as_text") || "Copy as text"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => downloadFile("markdown")} data-testid={`${testId}-md`}>
          <FileDown className="w-3.5 h-3.5 mr-2" />
          {t("download_markdown") || "Download .md"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => downloadFile("text")} data-testid={`${testId}-txt`}>
          <FileDown className="w-3.5 h-3.5 mr-2" />
          {t("download_text") || "Download .txt"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
